import qs from 'qs';
import { Button } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { pluralizeIssues } from '../../../../../shared/helpers';
import { Category } from '../../../../../shared/issuesV2';
import { issueTypeToCategory } from '../../../../helpers/issuesV2';
import Flex from '../../../generic/Flex';
import Icon, { icons } from '../../../generic/Icon';
import ISSUE_INDEX from '../../issues/IssueIndex';
import type { FilterByCategory, IssueType } from '../../../../../shared/issuesV2';
import type { ProjectGroup } from '../../../../types';

//  Determines the IssuesV2 path to the issue, with the correct filters applied
const getIssuePath = (issueType: IssueType, projectGroupId: number): string => {
  const category = issueTypeToCategory(issueType);
  const path = `/projects/group/${projectGroupId}/issues/${category}`;
  //  Vulns don't have any specific filters, so we can skip this part.
  if (category === Category.Vulnerability) {
    return path;
  }
  const params = qs.stringify({
    filter: {
      type: [issueType],
    },
  } as Partial<FilterByCategory<typeof category>>);

  return `${path}?${params}`;
};

type Props = {
  issuesCount: number;
  issueType: IssueType;
  projectGroup: ProjectGroup;
  router: InjectedRouter;
};

/**
 * Renders an issue "item" panel on the project summary page.
 */
const IssueListItem: VoidFunctionComponent<Props> = ({
  issuesCount,
  issueType,
  projectGroup,
  router,
}) => {
  const onItemClick = () => router.push(getIssuePath(issueType, projectGroup.id));

  const issueInfo = ISSUE_INDEX[issueType] || {
    title: `${issueType} Issue`,
    description: `FOSSA has discovered ${issuesCount} ${issueType} ${pluralizeIssues(issuesCount)}.`,
  };

  return (
    <li className="issue-list-item upshadow">
      {/* eslint-disable-next-line */}
      <span onClick={onItemClick}>
        <span className="icon-circle" />
        <Flex align="center" justify="space-between">
          <h4>
            {issuesCount}
            {' '}
            {issueInfo.title || issueType}
          </h4>
          <div className="text-right">
            <Button bsStyle="default">
              <Icon icon={icons.reviewFix} />
              <span className="margin-left-xs full">
                Review &amp; Fix
              </span>
            </Button>
            <span className="margin-left-xs">
              <Icon icon={icons.chevronDown} />
            </span>
          </div>
        </Flex>
        <p>{issueInfo.description}</p>
      </span>
    </li>
  );
};

export default IssueListItem;
